import { useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { segmentedControlButtonVariants } from './segmented-control-styles';
import { SegmentedCountBadge } from './SegmentedControl';

interface DetailTabsSectionTab {
    value: string;
    label: ReactNode;
    count?: number;
    content: ReactNode;
    action?: ReactNode;
}

interface DetailTabsSectionProps {
    tabs: DetailTabsSectionTab[];
    defaultValue?: string;
    value?: string;
    onValueChange?: (value: string) => void;
    className?: string;
    headerClassName?: string;
    contentClassName?: string;
}

export default function DetailTabsSection({
    tabs,
    defaultValue,
    value,
    onValueChange,
    className,
    headerClassName,
    contentClassName,
}: DetailTabsSectionProps) {
    const fallbackValue = tabs[0]?.value ?? '';
    const [internalValue, setInternalValue] = useState(defaultValue ?? fallbackValue);

    if (tabs.length === 0) {
        return null;
    }

    const requestedValue = value ?? internalValue;
    const resolvedValue = tabs.some((tab) => tab.value === requestedValue)
        ? requestedValue
        : fallbackValue;
    const activeTab = tabs.find((tab) => tab.value === resolvedValue) ?? tabs[0];

    const handleValueChange = (nextValue: string) => {
        if (value === undefined) {
            setInternalValue(nextValue);
        }

        onValueChange?.(nextValue);
    };

    return (
        <Tabs value={resolvedValue} onValueChange={handleValueChange} className="w-full">
            <Card
                data-slot="detail-tabs-section"
                className={cn(
                    'overflow-hidden rounded-[calc(var(--radius)+0.25rem)] border border-border/80 bg-card/95 shadow-elevation-1',
                    className,
                )}
            >
                <CardHeader className={cn('border-b border-border/70 p-6', headerClassName)}>
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                        <TabsList className="h-auto w-full flex-wrap items-stretch justify-start gap-2 rounded-none border-0 bg-transparent p-0 text-foreground shadow-none">
                            {tabs.map((tab) => (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className={cn(
                                        segmentedControlButtonVariants({
                                            variant: 'pills',
                                            active: false,
                                        }),
                                        'flex-1 data-[state=active]:border-brand/20 data-[state=active]:bg-brand-subtle data-[state=active]:text-brand data-[state=active]:shadow-sm sm:flex-none [&[data-state=active]_[data-slot=segmented-count]]:bg-card [&[data-state=active]_[data-slot=segmented-count]]:text-brand',
                                    )}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <span>{tab.label}</span>
                                        {typeof tab.count === 'number' && (
                                            <SegmentedCountBadge count={tab.count} active={false} />
                                        )}
                                    </span>
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        {activeTab.action && (
                            <div
                                data-slot="detail-tabs-actions"
                                className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end"
                            >
                                {activeTab.action}
                            </div>
                        )}
                    </div>
                </CardHeader>

                <CardContent className={cn('p-0', contentClassName)}>
                    {tabs.map((tab) => (
                        <TabsContent
                            key={tab.value}
                            value={tab.value}
                            className="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                        >
                            {tab.content}
                        </TabsContent>
                    ))}
                </CardContent>
            </Card>
        </Tabs>
    );
}
